import styled, { css } from 'styled-components';
import type React from 'react';
import { evoDropdownCard, evoDropdownOption, focusRing } from '@/app/components/search/styles/mixins.ts';

export const AutoCompleteRoot = styled.div<{ isFullScreen: boolean }>`
  width: 100%;
  ${({ isFullScreen }) =>
    !isFullScreen &&
    css`
      position: relative;
    `};
`;

export const AutoCompleteContainer = styled.div<{ isFullScreen: boolean }>`
  width: 100%;
  position: relative;
  ${({ isFullScreen }) =>
    isFullScreen &&
    css`
      background-color: #fff;
      height: 68px;
    `};

  * {
    box-sizing: border-box;
  }
`;

// The evo design's 20px icon sits 16px in; the current 19px one is centred on the same point.
const ICON_INSET = { evo: 16, current: 16.5 };

export const AutoCompleteInner = styled.div<{ isFullScreen: boolean; focus: boolean; $evo?: boolean }>`
  width: 100%;
  position: relative;
  background-color: ${({ theme, $evo }) =>
    $evo ? theme.kallisto.color.background.static.backgroundLayer01 : theme.kallisto.color.common.black[50]};
  display: flex;
  height: 52px;
  border-radius: 8px;

  > img {
    flex-shrink: 0;
    position: absolute;
    top: ${({ $evo }) => ($evo ? ICON_INSET.evo : ICON_INSET.current)}px;
    left: ${({ $evo }) => ($evo ? ICON_INSET.evo : ICON_INSET.current)}px;
  }

  ${({ isFullScreen }) =>
    isFullScreen &&
    css`
      padding-bottom: 16px;
      width: calc(100% - 32px);
      margin: 0 auto;

      input {
        margin: auto;
        display: block;
      }
    `};

  ${({ focus, $evo }) => {
    if ($evo)
      return (
        focus &&
        css`
          box-shadow: inset 0 0 0 2px ${({ theme }) => theme.commons.colors.gray800};
        `
      );
    return focus
      ? css`
          box-shadow: inset 0 0 0 1px ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
        `
      : css`
          &:hover {
            box-shadow: inset 0 0 0 3px ${({ theme }) => theme.kallisto.color.common.black[200]};
          }
        `;
  }}
`;

const baseSuggestionStyles = css`
  background: white;
  width: 100%;
  border-radius: 16px;
`;

const dropdownTop = ({ $evo }: { $evo?: boolean }) => ($evo ? 56 : 84);

const opensAbove = css`
  &[data-side='top'] {
    top: auto;
    bottom: calc(100% + 4px);
  }
`;

export const NoSuggestions = styled.div<{ isFullScreen: boolean; $evo?: boolean }>`
  ${baseSuggestionStyles};
  background: white;
  box-shadow: ${({ theme }): string => theme?.kallisto?.shadow?.largeDownShadow};
  border-radius: 16px;
  width: 100%;
  height: 52px;
  position: absolute;
  top: ${dropdownTop}px;
  left: 0;
  right: 0;
  padding: 16px;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.kallisto.color.common.semantic.error[500]};
  z-index: 1;

  ${({ $evo }) =>
    $evo &&
    css`
      ${evoDropdownCard};
      ${opensAbove};
      height: auto;

      em {
        display: block;
        padding: 8px 16px;
        font-style: normal;
      }
    `}

  ${({ isFullScreen, $evo }) =>
    isFullScreen &&
    css`
      width: calc(100% - 32px);
      top: 138px;
      left: 16px;
      right: unset;

      ${
        !$evo &&
        css`
          background: ${({ theme }) => theme.kallisto.color.background.static.backgroundErrorSubtle};
          outline: 2px solid ${({ theme }) => theme.kallisto.color.common.semantic.error[500]};
          outline-offset: -2px;
          border-radius: 16px;
        `
      }
    `}
`;

export const SuggestionsLabel = styled.label<{
  isFullScreen: boolean;
  focus: boolean;
  $hidden?: boolean;
  $evo?: boolean;
}>`
  position: absolute;
  left: 44px;
  font-style: normal;
  font-weight: 400;
  transition: all 0.2s ease-in-out;
  color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
  ${({ theme }) => theme.kallisto.mixins.font('Body')};

  ${({ focus }) =>
    focus
      ? css`
          height: 16px;
          white-space: nowrap;
          font-size: ${({ theme }) => theme.kallisto.font.size['150']};
        `
      : css`
          display: flex;
          align-items: center;
          height: 52px;
        `}

  ${({ $evo, focus, theme }) =>
    $evo &&
    focus &&
    css`
      top: 5px;
      height: auto;
      ${theme.kallisto.mixins.font('Caption')};
    `}

  ${({ $hidden }) =>
    $hidden &&
    css`
      opacity: 0;
    `}
`;

export const SuggestionInput = styled.input<{
  isFullScreen: boolean;
  value: string;
  focus: boolean;
  $evo?: boolean;
  $centred?: boolean;
  ref: React.MutableRefObject<HTMLInputElement | undefined>;
}>`
  display: block;
  border: none;
  border-radius: 8px;
  height: 52px;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  width: 100%;
  padding: 20px 44px 6px 44px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  background-color: transparent;

  ${({ value }) =>
    value.length > 0 &&
    css`
      padding-right: 44px;
    `}

  ${({ $evo }) =>
    $evo &&
    css`
      outline: none;
      padding-top: 23px;
      padding-bottom: 5px;
    `}

  ${({ $centred }) =>
    $centred &&
    css`
      padding-top: 14px;
      padding-bottom: 14px;
    `}
`;

export const SuggestionsContainerOuter = styled.div<{ isFullScreen: boolean; $evo?: boolean }>`
  ${baseSuggestionStyles};
  box-shadow: ${({ theme }): string => theme?.kallisto?.shadow?.largeDownShadow};
  position: absolute;
  top: ${dropdownTop}px;
  left: 0;
  overflow: hidden;
  z-index: 1;

  ${({ $evo, isFullScreen }) =>
    $evo &&
    css`
      ${evoDropdownCard};
      ${opensAbove};
      max-height: ${isFullScreen ? 'calc(100vh - 161px)' : '240px'};
      overflow-y: auto;
    `}

  ${({ isFullScreen }) =>
    isFullScreen &&
    css`
      width: calc(100% - 32px);
      left: 16px;
      top: 143px;
    `}
`;

export const SuggestionsContainer = styled.ul<{ isFullScreen: boolean; $evo?: boolean }>`
  max-height: 256px;
  overflow: auto;

  ${({ isFullScreen }) =>
    isFullScreen &&
    css`
      max-height: calc(100vh - 161px);
    `}

  ${({ $evo }) =>
    $evo &&
    css`
      max-height: none;
      overflow: visible;
    `}
`;

export const SuggestionContainer = styled.li<{ $evo?: boolean }>`
  padding: 8px 16px;
  cursor: pointer;

  ${({ $evo, theme }) =>
    $evo
      ? css`
          ${evoDropdownOption};

          &.suggestion-active {
            ${focusRing};
          }
        `
      : css`
          &.suggestion-active,
          &:hover {
            background-color: ${theme.kallisto.color.common.black[50]};
          }
        `}
`;

export const evoClearButton = css`
  top: 50%;
  right: 12px;
  display: flex;
  height: auto;
  padding: 4px;

  [data-library-icon] {
    color: ${({ theme }) => theme.kallisto.color.icon.static.iconPrimary};
  }

  &:focus-visible {
    ${focusRing};
    border-radius: 50%;
  }
`;

export const AutocompleteTagDelete = styled.button<{ isFullScreen: boolean; $evo?: boolean }>`
  position: absolute;
  top: 25px;
  transform: translateY(-50%);
  right: 16px;
  height: 16px;
  z-index: 22;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;

  [data-library-icon] {
    color: ${({ theme }) => theme.kallisto.color.common.black[200]};
    transition: color 0.2s ease;
  }

  &:hover {
    [data-library-icon] {
      color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
    }
  }

  ${({ $evo }) => $evo && evoClearButton}
`;
